import React, { useLayoutEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * The calm layer: the few rules that keep a screen quiet without hiding what
 * a reader needs.
 *
 *   1. A page, section or control says ONE sentence at rest. The rest of the
 *      explanation is one click away behind an (i), never deleted: a number
 *      still has to be defensible, it just does not argue on sight.
 *   2. Only the most important banner shows; the others fold into a count.
 *   3. While data loads, the page holds the shape it will have, so nothing
 *      jumps when it lands.
 *
 * `splitLede` is the one place a text is cut into "what shows" and "what
 * waits". Shared by PageHeader, GSection, PageSection and CardDeck.
 */

// Words that end in a full stop without ending a sentence.
const ABBREV = /(?:^|\s)(?:e\.g|i\.e|vs|etc|approx|incl|no|min|max|avg|Rs|Dr|Mr|Ms)\.$/i;

export function splitLede(text) {
  if (typeof text !== 'string') return [text, null];
  const s = text.trim();
  for (let i = 0; i < s.length - 1; i += 1) {
    const ch = s[i];
    if (ch !== '.' && ch !== '?' && ch !== '!') continue;
    if (s[i + 1] !== ' ') continue;
    const next = s[i + 2];
    if (!next || !/[A-Z0-9"“(‘']/.test(next)) continue;
    // "2.5 a week" and "e.g. Audit" are not sentence ends.
    if (ch === '.' && ABBREV.test(s.slice(0, i + 1))) continue;
    const first = s.slice(0, i + 1);
    const rest = s.slice(i + 2).trim();
    return [first, rest || null];
  }
  return [s, null];
}

/**
 * The info affordance. A click (or tap, or Enter) opens the rest of the
 * explanation in a popover; hover alone would leave phones with nothing.
 */
export function MoreInfo({ children, label = 'More about this', className, align = 'start' }) {
  if (!children) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label={label}
          className={cn('inline-flex size-[18px] shrink-0 translate-y-[3px] items-center justify-center rounded-full align-top text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}>
          <Info className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-[min(380px,calc(100vw-32px))] p-3.5 text-[13.5px] leading-relaxed text-muted-foreground">
        {children}
      </PopoverContent>
    </Popover>
  );
}

/**
 * One sentence at rest, the rest behind (i). Anything that is not a plain
 * string renders as given: JSX was written on purpose.
 */
export function Lede({ text, more, as: Tag = 'p', className }) {
  const [first, rest] = splitLede(text);
  const extra = [rest, more].filter(Boolean);
  if (!first && !extra.length) return null;
  return (
    <Tag className={className}>
      {first}
      {extra.length > 0 && (
        <>
          {' '}
          <MoreInfo>
            {extra.map((t, i) => (typeof t === 'string' ? <p key={i} className={i ? 'mt-2' : undefined}>{t}</p> : <React.Fragment key={i}>{t}</React.Fragment>))}
          </MoreInfo>
        </>
      )}
    </Tag>
  );
}

/**
 * Stacked notices, one at a time. Each child renders itself or nothing (they
 * decide from their own data), so the stack counts what actually rendered and
 * shows the first; the rest wait behind "N more". Order the children by
 * importance: the first one that renders is the one a reader sees.
 */
export function BannerStack({ children, className }) {
  const ref = useRef(null);
  const [n, setN] = useState(0);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const count = () => setN(el.children.length);
    count();
    const mo = new MutationObserver(count);
    mo.observe(el, { childList: true });
    return () => mo.disconnect();
  }, []);
  const more = n - 1;
  return (
    <div className={cn('flex items-stretch', className)}>
      <div ref={ref} className={cn('min-w-0 flex-1', !open && '[&>*:nth-child(n+2)]:hidden')}>{children}</div>
      {more > 0 && (
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          className="shrink-0 border-b border-l border-border bg-card px-3 text-[12.5px] font-medium text-foreground hover:bg-muted">
          {open ? 'Show one' : `+${more} more`}
        </button>
      )}
    </div>
  );
}

/**
 * Loading shapes that match what arrives: a tile row the height of GStatTile,
 * and panels the height of the cards under it. A spinner in a 64px box made
 * every page jump by a screen when its data landed.
 */
export function TileRowSkeleton({ n = 4, className }) {
  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 xl:grid-cols-4', className)} aria-hidden>
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="rounded-lg border border-[hsl(var(--card-border))] bg-card p-5">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="mt-3 h-7 w-20" />
          <Skeleton className="mt-3 h-3 w-32" />
        </div>
      ))}
    </div>
  );
}

export function PanelSkeleton({ height = 320, className, rows = 0 }) {
  return (
    <div className={cn('rounded-lg border border-[hsl(var(--card-border))] bg-card p-6', className)} style={{ minHeight: height }} aria-hidden>
      <Skeleton className="h-3.5 w-28" />
      <Skeleton className="mt-3 h-5 w-64 max-w-full" />
      {rows > 0 && (
        <div className="mt-6 space-y-3">
          {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
        </div>
      )}
    </div>
  );
}

/** The page body while it loads: a status line for screen readers, shapes for eyes. */
export function PageSkeleton({ tiles = 4, panels = [320], className }) {
  return (
    <div className={cn('space-y-5', className)} role="status" aria-label="Loading">
      {tiles > 0 && <TileRowSkeleton n={tiles} />}
      {panels.map((h, i) => <PanelSkeleton key={i} height={h} rows={h >= 360 ? 5 : 0} />)}
    </div>
  );
}
